import React, { useState, useEffect } from 'react';
import promocodeService from '../../utils/promocodeService';
import PromocodeGenerator from '../../utils/promocodeGenerator';
import PromocodeDecoder from '../../utils/promocodeDecoder';
import { PROMOCODE_CONFIG, PromocodeHelpers } from '../../config/promocode.js';

const PromocodeGeneratorComponent = () => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [generatedCodes, setGeneratedCodes] = useState([]);

  const [formData, setFormData] = useState({
    platform: '', // No default platform - must be selected
    planType: 'lifetime',
    credits: 10000,
    expiryYear: new Date().getFullYear() + 1,
    quantity: 1,
    customPrefix: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  // Load platforms on component mount
  useEffect(() => {
    loadPlatforms();
  }, []);

  const loadPlatforms = () => {
    const activePlatforms = PromocodeHelpers.getActivePlatforms();
    setPlatforms(activePlatforms);
    
    // If no platforms exist, show warning
    if (activePlatforms.length === 0) {
      setMessage('No platforms available. Please add a platform first before generating promocodes.');
      setMessageType('warning');
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setMessage('');

    // Validate that a platform is selected
    if (!formData.platform) {
      setMessage('Please select a platform before generating promocodes.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    // Validate that platforms exist
    if (platforms.length === 0) {
      setMessage('No platforms available. Please add a platform first before generating promocodes.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      // Validate form data
      const validation = PromocodeGenerator.validateConfig(formData);
      if (!validation.isValid) {
        setMessage(`Validation failed: ${validation.errors.join(', ')}`);
        setMessageType('error');
        setLoading(false);
        return;
      }

      // Generate promocodes
      const codes = PromocodeGenerator.generateBulk(formData, formData.quantity);
      setGeneratedCodes(codes);
      
      setMessage(`Successfully generated ${codes.length} promocodes`);
      setMessageType('success');
    } catch (error) {
      setMessage('Failed to generate promocodes');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToDatabase = async () => {
    if (generatedCodes.length === 0) {
      setMessage('No codes to save');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const result = await promocodeService.bulkCreatePromocodes(generatedCodes, formData);
      
      if (result.success) {
        setMessage(`Successfully saved ${result.count} promocodes to database and exported CSV file`);
        setMessageType('success');
        
        // Automatically export CSV after saving to database
        handleExportCodes();
        
        setGeneratedCodes([]);
      } else {
        setMessage(result.error);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to save promocodes');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCodes = () => {
    if (generatedCodes.length === 0) return;

    const csvContent = [
      'Promocode,Platform,Plan Type,Start Date,End Date,Credits',
      ...generatedCodes.map(code => {
        // Use form data directly instead of decoding
        const platformName = formData.platform.charAt(0).toUpperCase() + formData.platform.slice(1).toLowerCase();
        const planTypeName = formData.planType.charAt(0).toUpperCase() + formData.planType.slice(1).toLowerCase();
        return `${code},${platformName},${planTypeName},${formData.startDate},${formData.endDate},${formData.credits}`;
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `generated_promocodes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCopyCodes = () => {
    if (generatedCodes.length === 0) return;
    
    navigator.clipboard.writeText(generatedCodes.join('\n'));
    setMessage('Codes copied to clipboard');
    setMessageType('success');
  };

  const platformOptions = PromocodeHelpers.getActivePlatforms().map(p => ({
    value: p.name.toLowerCase(),
    label: p.name
  }));

  const planTypeOptions = [
    { value: 'lifetime', label: 'Lifetime' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  // Format is now fixed to structured - no options needed

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        Promocode Generator
      </h3>

      {/* Warning when no platforms exist */}
      {platforms.length === 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                No Platforms Available
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>You need to add at least one platform before generating promocodes. Please go to the Platform Manager to add platforms first.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generation Form */}
      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Platform <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.platform}
              onChange={(e) => setFormData({...formData, platform: e.target.value})}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                platforms.length === 0 ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              required
              disabled={platforms.length === 0}
            >
              <option value="">Select a platform</option>
              {platformOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {platforms.length === 0 && (
              <p className="text-red-600 text-sm mt-1">
                No platforms available. Please add a platform first.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan Type
            </label>
            <select
              value={formData.planType}
              onChange={(e) => setFormData({...formData, planType: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {planTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credits Amount
            </label>
            <input
              type="number"
              value={formData.credits}
              onChange={(e) => setFormData({...formData, credits: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min={100}
              max={10000000}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiry Year
            </label>
            <input
              type="number"
              value={formData.expiryYear}
              onChange={(e) => setFormData({...formData, expiryYear: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min={new Date().getFullYear()}
              max={new Date().getFullYear() + 10}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity
            </label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min={1}
              max={1000}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custom Prefix (Optional)
            </label>
            <input
              type="text"
              value={formData.customPrefix}
              onChange={(e) => setFormData({...formData, customPrefix: e.target.value.toUpperCase()})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={4}
              placeholder="e.g., VIP"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({...formData, startDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({...formData, endDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

        </div>

        <div className="flex space-x-4">
          <button
            onClick={handleGenerate}
            disabled={loading || platforms.length === 0 || !formData.platform}
            className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {loading ? 'Generating...' : 'Generate Codes'}
          </button>

          {generatedCodes.length > 0 && (
            <>
              <button
                onClick={handleSaveToDatabase}
                disabled={loading}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save to Database'}
              </button>
              <button
                onClick={handleExportCodes}
                className="bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                Export CSV
              </button>
              <button
                onClick={handleCopyCodes}
                className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Copy Codes
              </button>
            </>
          )}
        </div>
      </div>

      {/* Generated Codes Display */}
      {generatedCodes.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3">
            Generated Codes ({generatedCodes.length})
          </h4>
          <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {generatedCodes.map((code, index) => (
                <div key={index} className="bg-white p-2 rounded border text-sm font-mono">
                  {code}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div className={`mt-4 p-3 rounded-md ${
          messageType === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Information */}
      <div className="mt-6 text-xs text-gray-500">
        <p>• Structured format: [PLATFORM][PLAN][CREDITS][EXPIRY][RANDOM]</p>
        <p>• All generated codes are validated before saving</p>
        <p>• Codes can be exported to CSV or copied to clipboard</p>
      </div>
    </div>
  );
};

export default PromocodeGeneratorComponent;
